import React, { useState, useRef, useEffect } from "react";
import { Menu, Bell, Search, User, LogOut, Settings, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import "./Header.css";

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: "New Book Available",
      message: "Advanced Mathematics textbook is now available for borrowing",
      time: "2 hours ago",
      read: false,
      type: "library",
    },
    {
      id: 2,
      title: "Transportation Update",
      message: "Route 3 schedule has been updated for next week",
      time: "4 hours ago",
      read: false,
      type: "transportation",
    },
    {
      id: 3,
      title: "Cafeteria Menu",
      message: "New healthy meal options added to today's menu",
      time: "1 day ago",
      read: true,
      type: "cafeteria",
    },
  ]);

  const searchRef = useRef(null);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);

  // Mock search data
  const searchData = [
    { id: 1, title: "Advanced Mathematics", type: "book", category: "library" },
    { id: 2, title: "Emma Wilson", type: "student", category: "student" },
    {
      id: 3,
      title: "Route 3 - Downtown",
      type: "route",
      category: "transportation",
    },
    { id: 4, title: "Physics Fundamentals", type: "book", category: "library" },
    { id: 5, title: "Michael Chen", type: "teacher", category: "teacher" },
    {
      id: 6,
      title: "Route 1 - School Campus",
      type: "route",
      category: "transportation",
    },
  ];

  // Handle search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length > 2) {
      const results = searchData.filter((item) =>
        item.title.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  // Handle search result click
  const handleSearchResultClick = (result) => {
    setSearchQuery("");
    setShowSearchResults(false);
    // Navigate based on category
    switch (result.category) {
      case "library":
        navigate("/library");
        break;
      case "transportation":
        navigate("/transportation");
        break;
      case "student":
      case "teacher":
        navigate("/admin");
        break;
      default:
        break;
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === notification.id ? { ...notif, read: true } : notif
      )
    );

    // Navigate based on notification type
    switch (notification.type) {
      case "library":
        navigate("/library");
        break;
      case "transportation":
        navigate("/transportation");
        break;
      case "cafeteria":
        navigate("/cafeteria");
        break;
      default:
        break;
    }
    setShowNotifications(false);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/");
    setShowUserMenu(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const unreadNotifications = notifications.filter((n) => !n.read).length;

  return (
    <header className="header">
      <div className="header-left">
        <button className="menu-btn" onClick={onMenuClick}>
          <Menu size={24} />
        </button>
        <div className="header-logo">
          <img src="/logo.png" alt="GISS Logo" className="header-logo-image" />
        </div>
        <div className="search-bar" ref={searchRef}>
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search books, students, routes..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => searchQuery.length > 2 && setShowSearchResults(true)}
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => {
                setSearchQuery("");
                setShowSearchResults(false);
              }}
            >
              <X size={16} />
            </button>
          )}

          {showSearchResults && searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((result) => (
                <div
                  key={result.id}
                  className="search-result-item"
                  onClick={() => handleSearchResultClick(result)}
                >
                  <div className="search-result-content">
                    <span className="search-result-title">{result.title}</span>
                    <span className="search-result-type">{result.type}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        <div className="notification-container" ref={notificationRef}>
          <button
            className="notification-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell size={20} />
            {unreadNotifications > 0 && (
              <span className="notification-badge">{unreadNotifications}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                <button
                  className="notification-close"
                  onClick={() => setShowNotifications(false)}
                >
                  <X size={16} />
                </button>
              </div>
              <div className="notification-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${
                      !notification.read ? "unread" : ""
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-content">
                      <h4>{notification.title}</h4>
                      <p>{notification.message}</p>
                      <span className="notification-time">
                        {notification.time}
                      </span>
                    </div>
                    {!notification.read && (
                      <div className="notification-dot"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="user-menu-container" ref={userMenuRef}>
          <div
            className="user-menu"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="user-avatar">{user?.avatar}</div>
            <div className="user-info">
              <span className="user-name">{user?.name}</span>
              <span className="user-role">{user?.role}</span>
            </div>
          </div>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">{user?.avatar}</div>
                <div className="user-dropdown-info">
                  <span className="user-dropdown-name">{user?.name}</span>
                  <span className="user-dropdown-email">{user?.email}</span>
                </div>
              </div>
              <div className="user-dropdown-menu">
                <button className="dropdown-item">
                  <User size={16} />
                  Profile
                </button>
                <button className="dropdown-item">
                  <Settings size={16} />
                  Settings
                </button>
                <hr className="dropdown-divider" />
                <button className="dropdown-item logout" onClick={handleLogout}>
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
